import { useState, createContext, useContext } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Helmet } from "react-helmet-async";
import { Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import {
  ArrowRight, ArrowDown, Check, CheckCircle2, X, Loader2, Rocket, Sparkles,
  Layers, Cpu, GitBranch, PenTool, Mic, Database, Settings2,
  PhoneCall, Megaphone, HelpCircle, ClipboardList, BarChart3, Plus, Minus,
} from "lucide-react";
import { SITE } from "@/config";
import SeatsBadge from "@/components/SeatsBadge";
import SessionBooking from "@/components/SessionBooking";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const GOLD = "#D9A441";
const TEAL = "#5EC8BE";
const ApplyContext = createContext(() => {});

const Reveal = ({ children, delay = 0, className = "" }) => (
  <motion.div
    initial={{ opacity: 0, y: 28 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true, margin: "-70px" }}
    transition={{ duration: 0.7, delay, ease: [0.16, 1, 0.3, 1] }}
    className={className}
  >
    {children}
  </motion.div>
);

const Overline = ({ children, color = GOLD }) => (
  <p className="text-xs uppercase tracking-[0.25em] text-neutral-500 mb-6 flex items-center gap-3">
    <span className="h-px w-8" style={{ background: color }} />
    {children}
  </p>
);

const Badge = ({ children }) => (
  <span className="inline-flex items-center gap-1.5 rounded-full border hairline bg-black/30 px-3 py-1 text-xs text-neutral-300">
    <span className="h-1.5 w-1.5 rounded-full" style={{ background: TEAL }} />
    {children}
  </span>
);

const ApplyButton = ({ testid, className = "", children }) => {
  const open = useContext(ApplyContext);
  return (
    <button
      type="button"
      onClick={open}
      data-testid={testid}
      className={`group inline-flex items-center gap-2 rounded-full bg-white text-black text-sm font-medium px-7 py-3.5 hover:bg-neutral-200 transition-colors duration-300 ${className}`}
    >
      <Rocket size={16} />
      {children || "Apply for BUILD // 001"}
      <ArrowRight size={16} className="group-hover:translate-x-0.5 transition-transform duration-300" />
    </button>
  );
};

const occupations = ["Business Owner", "Agency Owner", "Freelancer", "Entrepreneur", "Real Estate Professional", "Sales Professional", "Student", "Other"];

/* ---------------- Apply Modal ---------------- */
const ApplyModal = ({ open, onClose }) => {
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [form, setForm] = useState({
    first_name: "", last_name: "", email: "", whatsapp: "",
    country: "", occupation: "Business Owner", reason: "", agree: false,
  });
  const update = (e) => {
    const { name, value, type, checked } = e.target;
    setForm((f) => ({ ...f, [name]: type === "checkbox" ? checked : value }));
  };
  const canSubmit = form.first_name && form.email && form.whatsapp && form.agree && !loading;

  const submit = async () => {
    setLoading(true);
    try {
      await axios.post(`${API}/build/apply`, {
        first_name: form.first_name, last_name: form.last_name, email: form.email,
        whatsapp: form.whatsapp, country: form.country, occupation: form.occupation,
        reason: form.reason, product_id: "build_001",
      });
      setSubmitted(true);
    } catch {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    onClose();
    setTimeout(() => {
      setSubmitted(false);
      setForm({ first_name: "", last_name: "", email: "", whatsapp: "", country: "", occupation: "Business Owner", reason: "", agree: false });
    }, 300);
  };

  const inputCls = "w-full bg-transparent border-b border-white/15 py-2.5 placeholder:text-neutral-600 focus:border-white outline-none transition-colors duration-300";

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
          className="fixed inset-0 z-[80] flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm"
          onClick={handleClose}
          data-testid="apply-modal"
        >
          <motion.div
            initial={{ opacity: 0, y: 24, scale: 0.98 }} animate={{ opacity: 1, y: 0, scale: 1 }} exit={{ opacity: 0, y: 12, scale: 0.99 }}
            transition={{ duration: 0.35, ease: [0.16, 1, 0.3, 1] }}
            onClick={(e) => e.stopPropagation()}
            className="relative w-full max-w-lg max-h-[90vh] overflow-auto rounded-3xl border hairline bg-[#0B0B0D] p-7 md:p-9"
          >
            <button onClick={handleClose} data-testid="apply-close" aria-label="Close" className="absolute top-5 right-5 text-neutral-500 hover:text-white transition-colors duration-300">
              <X size={20} />
            </button>

            {submitted ? (
              <div data-testid="apply-success" className="text-center py-6">
                <motion.span
                  initial={{ scale: 0.6, opacity: 0 }} animate={{ scale: 1, opacity: 1 }}
                  transition={{ duration: 0.4, ease: [0.16, 1, 0.3, 1] }}
                  className="inline-flex h-16 w-16 items-center justify-center rounded-full mx-auto"
                  style={{ background: "rgba(94,200,190,0.12)", color: TEAL }}
                >
                  <CheckCircle2 size={32} />
                </motion.span>
                <h3 className="text-2xl font-bold tracking-tight mt-6">Application received!</h3>
                <p className="text-neutral-400 mt-3 leading-relaxed max-w-sm mx-auto">
                  Thanks{form.first_name ? `, ${form.first_name}` : ""}! We&apos;ll reach out to you soon through email or on WhatsApp with your invitation to BUILD // 001.
                </p>

                <SessionBooking prefill={{ first_name: form.first_name, email: form.email, whatsapp: form.whatsapp }} />

                <button
                  data-testid="apply-done"
                  onClick={handleClose}
                  className="mt-8 w-full flex items-center justify-center gap-2 rounded-full bg-white text-black text-sm font-medium py-3.5 hover:bg-neutral-200 transition-colors duration-300"
                >
                  Done
                </button>
              </div>
            ) : (
              <div data-testid="apply-form">
                <span className="text-xs uppercase tracking-[0.28em]" style={{ color: GOLD }}>BUILD // 001</span>
                <h3 className="text-2xl font-bold tracking-tight mt-2">Apply for BUILD</h3>
                <p className="text-neutral-400 mt-1 mb-7">Tell me a little about yourself and I’ll be in touch with your invitation.</p>
                <div className="space-y-5">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-xs uppercase tracking-[0.15em] text-neutral-500 mb-2">First name *</label>
                      <input data-testid="af-first" name="first_name" value={form.first_name} onChange={update} className={inputCls} placeholder="Anubhav" />
                    </div>
                    <div>
                      <label className="block text-xs uppercase tracking-[0.15em] text-neutral-500 mb-2">Last name</label>
                      <input data-testid="af-last" name="last_name" value={form.last_name} onChange={update} className={inputCls} placeholder="Sahni" />
                    </div>
                  </div>
                  <div>
                    <label className="block text-xs uppercase tracking-[0.15em] text-neutral-500 mb-2">Email address *</label>
                    <input data-testid="af-email" type="email" name="email" value={form.email} onChange={update} className={inputCls} placeholder="you@email.com" />
                  </div>
                  <div>
                    <label className="block text-xs uppercase tracking-[0.15em] text-neutral-500 mb-2">WhatsApp number *</label>
                    <input data-testid="af-whatsapp" name="whatsapp" value={form.whatsapp} onChange={update} className={inputCls} placeholder="+91 98765 43210" />
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-xs uppercase tracking-[0.15em] text-neutral-500 mb-2">Country</label>
                      <input data-testid="af-country" name="country" value={form.country} onChange={update} className={inputCls} placeholder="India" />
                    </div>
                    <div>
                      <label className="block text-xs uppercase tracking-[0.15em] text-neutral-500 mb-2">You are a</label>
                      <select data-testid="af-occupation" name="occupation" value={form.occupation} onChange={update} className={`${inputCls} appearance-none`}>
                        {occupations.map((o) => <option key={o} value={o} className="bg-[#0B0B0D]">{o}</option>)}
                      </select>
                    </div>
                  </div>
                  <div>
                    <label className="block text-xs uppercase tracking-[0.15em] text-neutral-500 mb-2">Why do you want to join BUILD?</label>
                    <textarea data-testid="af-reason" name="reason" rows={3} value={form.reason} onChange={update} className={`${inputCls} resize-none`} placeholder="A few lines about what you want to build…" />
                  </div>
                  <label className="flex items-start gap-3 text-sm text-neutral-400 cursor-pointer">
                    <input data-testid="af-agree" type="checkbox" name="agree" checked={form.agree} onChange={update} className="mt-1 accent-white h-4 w-4" />
                    <span>I understand BUILD is a live, hands-on learning experience where I build my own AI application. It is not a done-for-you service.</span>
                  </label>
                </div>
                <button
                  data-testid="apply-submit"
                  disabled={!canSubmit}
                  onClick={submit}
                  className="mt-7 w-full flex items-center justify-center gap-2 rounded-full text-black text-sm font-semibold py-4 transition-transform duration-300 hover:scale-[1.01] disabled:opacity-40 disabled:cursor-not-allowed"
                  style={{ background: GOLD }}
                >
                  {loading ? <><Loader2 size={16} className="animate-spin" /> Submitting…</> : <>Submit application <ArrowRight size={16} /></>}
                </button>
              </div>
            )}
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

/* ---------------- Nav ---------------- */
const Nav = () => (
  <nav data-testid="build-nav" className="fixed top-0 inset-x-0 z-50 backdrop-blur-md bg-black/30 border-b hairline">
    <div className="max-w-5xl mx-auto px-5 md:px-8 h-16 flex items-center justify-between gap-4">
      <Link to="/" data-testid="build-logo" className="text-sm font-semibold tracking-tight">Anubhav Sahni</Link>
      <div className="hidden md:flex items-center gap-8 text-sm text-neutral-400">
        <Link to="/" className="hover:text-white transition-colors duration-300">Home</Link>
        <span className="text-white">BUILD</span>
      </div>
      <ApplyButton testid="nav-apply" className="!px-4 !py-2">Apply</ApplyButton>
    </div>
  </nav>
);

/* ---------------- Hero ---------------- */
const heroBadges = ["Beginner Friendly", "Live Guided", "Limited Seats", "Applications Open"];
const Hero = () => (
  <section data-testid="build-hero" className="relative overflow-hidden pt-36 pb-24 md:pt-44 md:pb-28">
    <div
      className="absolute inset-0 -z-10 opacity-[0.4]"
      style={{
        backgroundImage:
          "linear-gradient(to right, rgba(255,255,255,0.045) 1px, transparent 1px), linear-gradient(to bottom, rgba(255,255,255,0.045) 1px, transparent 1px)",
        backgroundSize: "56px 56px",
        maskImage: "radial-gradient(ellipse 70% 60% at 50% 0%, black 40%, transparent 80%)",
        WebkitMaskImage: "radial-gradient(ellipse 70% 60% at 50% 0%, black 40%, transparent 80%)",
      }}
    />
    <div className="absolute -top-24 left-1/4 w-[420px] h-[280px] rounded-full blur-[150px] opacity-25 -z-10" style={{ background: TEAL }} />
    <div className="absolute -top-10 right-1/4 w-[420px] h-[280px] rounded-full blur-[150px] opacity-20 -z-10" style={{ background: GOLD }} />

    <div className="relative max-w-5xl mx-auto px-5 md:px-8 text-center">
      <motion.span
        initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}
        className="inline-flex items-center gap-2 rounded-full border hairline bg-white/[0.03] px-4 py-1.5 text-xs uppercase tracking-[0.2em] text-neutral-300"
      >
        <Sparkles size={13} style={{ color: TEAL }} /> Learn AI by building
      </motion.span>

      <motion.h1
        initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8, delay: 0.08, ease: [0.16, 1, 0.3, 1] }}
        className="text-6xl sm:text-7xl lg:text-8xl font-bold tracking-[-0.04em] leading-[0.95] mt-8"
      >
        BUILD
      </motion.h1>
      <motion.p
        initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, delay: 0.18 }}
        className="mt-5 text-2xl md:text-3xl font-medium text-neutral-200"
      >
        Learn AI by Building Real Applications.
      </motion.p>

      <motion.p
        initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.7, delay: 0.3 }}
        className="mt-7 text-lg text-neutral-400 leading-relaxed max-w-2xl mx-auto"
      >
        Every BUILD is a live, guided experience where you’ll build a real AI application from scratch alongside me. No endless theory. No confusing technical jargon. Just practical AI that you can understand, customize, and apply to your own business.
      </motion.p>

      <motion.div
        initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, delay: 0.42 }}
        className="mt-10 flex justify-center"
      >
        <ApplyButton testid="hero-apply" />
      </motion.div>

      <div className="mt-16 rounded-3xl border hairline p-6 md:p-8 text-left" style={{ background: "linear-gradient(135deg, rgba(94,200,190,0.06), rgba(217,164,65,0.05))" }}>
        <span className="text-xs uppercase tracking-[0.28em]" style={{ color: GOLD }}>BUILD // 001</span>
        <h2 className="text-2xl md:text-3xl font-bold tracking-tight mt-3">Build Your First AI Voice Agent</h2>
        <div className="flex flex-wrap items-center gap-2 mt-5">
          <SeatsBadge />
          {heroBadges.map((b) => <Badge key={b}>{b}</Badge>)}
        </div>
      </div>
    </div>
  </section>
);

/* ---------------- Blueprint ---------------- */
const blueprint = [
  { icon: Layers, label: "Foundation" },
  { icon: Cpu, label: "AI Tools" },
  { icon: GitBranch, label: "Workflow" },
  { icon: PenTool, label: "Prompt Design" },
  { icon: Mic, label: "Voice Agent" },
  { icon: Database, label: "CRM" },
  { icon: Rocket, label: "Deployment" },
];
const Blueprint = () => (
  <section data-testid="build-blueprint" className="max-w-5xl mx-auto px-5 md:px-8 py-24 md:py-32 border-t hairline">
    <Reveal><Overline color={TEAL}>Blueprint</Overline></Reveal>
    <Reveal>
      <h2 className="text-3xl md:text-5xl font-bold tracking-tight max-w-2xl leading-tight">
        We’re not just learning. We’re engineering something.
      </h2>
    </Reveal>
    <div className="mt-14">
      {/* Desktop */}
      <div className="hidden lg:flex items-center gap-2">
        {blueprint.map((b, i) => {
          const Icon = b.icon;
          return (
            <div key={b.label} className="flex items-center gap-2 flex-1">
              <Reveal delay={i * 0.08} className="flex-1">
                <div className="rounded-2xl border hairline bg-white/[0.03] p-5 text-center hover:bg-white/[0.05] transition-colors duration-300">
                  <span className="inline-flex h-11 w-11 items-center justify-center rounded-xl border hairline mx-auto" style={{ color: i % 2 ? TEAL : GOLD }}>
                    <Icon size={20} />
                  </span>
                  <p className="mt-4 text-sm font-medium leading-snug">{b.label}</p>
                </div>
              </Reveal>
              {i < blueprint.length - 1 && <ArrowRight size={16} className="text-neutral-600 shrink-0" />}
            </div>
          );
        })}
      </div>
      {/* Mobile */}
      <div className="lg:hidden space-y-3">
        {blueprint.map((b, i) => {
          const Icon = b.icon;
          return (
            <Reveal key={b.label} delay={i * 0.05}>
              <div className="flex items-center gap-4">
                <span className="inline-flex h-11 w-11 items-center justify-center rounded-xl border hairline shrink-0" style={{ color: i % 2 ? TEAL : GOLD }}>
                  <Icon size={20} />
                </span>
                <div className="flex-1 rounded-xl border hairline bg-white/[0.03] px-4 py-3">
                  <p className="font-medium">{b.label}</p>
                </div>
              </div>
              {i < blueprint.length - 1 && <div className="flex pl-4"><ArrowDown size={16} className="text-neutral-600 my-1" /></div>}
            </Reveal>
          );
        })}
      </div>
    </div>
  </section>
);

/* ---------------- Featured image band ---------------- */
const FeaturedBand = () => (
  <section data-testid="build-featured" className="max-w-5xl mx-auto px-5 md:px-8 py-24 md:py-32 border-t hairline">
    <Reveal>
      <div className="relative rounded-3xl border hairline overflow-hidden" style={{ background: "linear-gradient(135deg, rgba(94,200,190,0.06), rgba(217,164,65,0.05))" }}>
        <div className="grid lg:grid-cols-2">
          <div className="p-8 md:p-12 flex flex-col justify-center">
            <span className="text-xs uppercase tracking-[0.28em]" style={{ color: GOLD }}>BUILD // 001</span>
            <h2 className="text-3xl md:text-4xl font-bold tracking-tight mt-4 leading-tight">A working AI Voice Agent, built live — 1:1.</h2>
            <p className="mt-5 text-neutral-300 leading-relaxed">
              By the end of the session, you’ll have your own AI Voice Agent that calls leads, presents your business, qualifies prospects, and updates your CRM — and you’ll understand exactly how every piece fits together.
            </p>
            <ApplyButton testid="featured-apply" className="mt-8 self-start" />
          </div>
          <div className="border-t lg:border-t-0 lg:border-l hairline bg-black flex items-center justify-center">
            <picture>
              <source srcSet="/build-001.webp" type="image/webp" />
              <img
                src="/build-001.jpg"
                alt="BUILD // 001 — Build your first AI Voice Agent"
                width="1000"
                height="1250"
                loading="lazy"
                decoding="async"
                data-testid="build-001-image"
                className="block w-full h-auto"
              />
            </picture>
          </div>
        </div>
      </div>
    </Reveal>
  </section>
);

/* ---------------- What is BUILD ---------------- */
const WhatIsBuild = () => (
  <section data-testid="build-what-is" className="max-w-5xl mx-auto px-5 md:px-8 py-24 md:py-32 border-t hairline">
    <Reveal><Overline>What is BUILD?</Overline></Reveal>
    <div className="grid md:grid-cols-2 gap-12 md:gap-16">
      <Reveal>
        <p className="text-lg md:text-xl text-neutral-300 leading-relaxed">
          BUILD is a practical learning platform designed for beginners, entrepreneurs, freelancers, business owners, and curious builders who want to learn AI by creating real applications.
        </p>
      </Reveal>
      <Reveal delay={0.1}>
        <div className="space-y-5 text-lg text-neutral-400 leading-relaxed">
          <p>Instead of consuming hours of video lessons, you’ll build a working AI application step by step and understand how the entire workflow comes together.</p>
          <p className="text-neutral-200">The goal is not just to teach tools. The goal is to teach you how to think, design, and build AI applications that solve real business problems.</p>
        </div>
      </Reveal>
    </div>
  </section>
);

/* ---------------- What You’ll Learn ---------------- */
const learnItems = [
  "What AI Agents are", "Which modern AI tools to use", "How AI tools connect together",
  "Prompt Engineering fundamentals", "How AI Voice works", "How to structure conversations",
  "How to customize AI for your own business", "CRM integration basics", "Testing your AI",
  "Deploying your first AI application",
];
const WhatYoullLearn = () => (
  <section data-testid="build-learn" className="max-w-5xl mx-auto px-5 md:px-8 py-24 md:py-32 border-t hairline">
    <Reveal><Overline color={TEAL}>What you’ll learn</Overline></Reveal>
    <Reveal><h2 className="text-3xl md:text-5xl font-bold tracking-tight max-w-2xl leading-tight">During BUILD // 001 you’ll learn:</h2></Reveal>
    <div className="grid sm:grid-cols-2 gap-3 mt-12">
      {learnItems.map((item, i) => (
        <Reveal key={item} delay={(i % 2) * 0.06}>
          <div className="flex items-center gap-3 rounded-xl border hairline bg-white/[0.03] px-5 py-4">
            <Check size={16} style={{ color: TEAL }} className="shrink-0" />
            <p className="text-neutral-200">{item}</p>
          </div>
        </Reveal>
      ))}
    </div>
    <p className="mt-8 text-neutral-400">The focus is understanding the complete workflow.</p>
  </section>
);

/* ---------------- What You’ll Build ---------------- */
const buildOutcomes = [
  { icon: PhoneCall, text: "Call leads using AI voice" },
  { icon: Megaphone, text: "Present your business using your own script" },
  { icon: HelpCircle, text: "Ask qualification questions" },
  { icon: ClipboardList, text: "Capture customer responses" },
  { icon: Database, text: "Send collected information back to your CRM" },
  { icon: Settings2, text: "Be customized for your own business or use case" },
];
const WhatYoullBuild = () => (
  <section data-testid="build-outcomes" className="max-w-5xl mx-auto px-5 md:px-8 py-24 md:py-32 border-t hairline">
    <Reveal><Overline>What you’ll build</Overline></Reveal>
    <Reveal>
      <h2 className="text-3xl md:text-5xl font-bold tracking-tight max-w-2xl leading-tight">
        During BUILD // 001 we’ll build an AI Voice Agent together.
      </h2>
    </Reveal>
    <p className="mt-6 text-lg text-neutral-400 max-w-2xl">By the end of the session your AI Voice Agent will be able to:</p>
    <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4 mt-12">
      {buildOutcomes.map((o, i) => {
        const Icon = o.icon;
        return (
          <Reveal key={o.text} delay={(i % 3) * 0.06}>
            <div className="h-full rounded-2xl border hairline bg-white/[0.03] p-6 hover:bg-white/[0.05] transition-colors duration-300">
              <span className="inline-flex h-10 w-10 items-center justify-center rounded-xl border hairline" style={{ color: i % 2 ? TEAL : GOLD }}>
                <Icon size={18} />
              </span>
              <p className="mt-5 text-neutral-200 leading-relaxed">{o.text}</p>
            </div>
          </Reveal>
        );
      })}
    </div>
    <p className="mt-8 text-neutral-400 max-w-2xl">This Build teaches the process of creating AI applications — not just copying prompts.</p>
  </section>
);

/* ---------------- Imagine This ---------------- */
const ImagineThis = () => (
  <section data-testid="build-imagine" className="max-w-5xl mx-auto px-5 md:px-8 py-24 md:py-32 border-t hairline">
    <Reveal>
      <div className="rounded-3xl border hairline p-8 md:p-14" style={{ background: "linear-gradient(135deg, rgba(217,164,65,0.06), rgba(94,200,190,0.05))" }}>
        <Overline color={TEAL}>Imagine this</Overline>
        <div className="space-y-4 text-lg md:text-xl text-neutral-300 leading-relaxed max-w-3xl">
          <p>Imagine uploading hundreds or thousands of leads.</p>
          <p className="text-neutral-400">Instead of manually calling every person… your AI Voice Agent starts calling using the script you’ve created.</p>
          <p className="text-neutral-400">It introduces your business. Asks qualification questions. Captures customer responses. Updates your CRM.</p>
          <p className="text-neutral-200">By the time it’s finished, you’ll already know which prospects are worth following up with.</p>
        </div>
        <p className="mt-8 text-neutral-400 max-w-3xl">
          The purpose isn’t to replace people. It’s to automate repetitive work so you can spend more time having meaningful conversations with qualified prospects.
        </p>
      </div>
    </Reveal>
  </section>
);

/* ---------------- Why BUILD ---------------- */
const WhyBuild = () => (
  <section data-testid="build-why" className="max-w-5xl mx-auto px-5 md:px-8 py-24 md:py-32 border-t hairline">
    <Reveal><Overline>Why BUILD?</Overline></Reveal>
    <div className="grid md:grid-cols-2 gap-12 md:gap-16">
      <Reveal>
        <h2 className="text-3xl md:text-5xl font-bold tracking-tight leading-tight">
          Most AI courses teach concepts. BUILD teaches execution.
        </h2>
      </Reveal>
      <Reveal delay={0.1}>
        <div className="space-y-5 text-lg text-neutral-400 leading-relaxed">
          <p>You won’t simply watch tutorials. You’ll understand how AI applications are created by building one yourself.</p>
          <p className="text-neutral-200">By the end of BUILD // 001 you’ll understand the complete workflow behind building AI Voice Agents.</p>
        </div>
      </Reveal>
    </div>
  </section>
);

/* ---------------- Who Should Join ---------------- */
const audiences = ["Business Owners", "Agencies", "Freelancers", "Entrepreneurs", "Real Estate Professionals", "Sales Teams", "Beginners interested in AI"];
const WhoShouldJoin = () => (
  <section data-testid="build-who" className="max-w-5xl mx-auto px-5 md:px-8 py-24 md:py-32 border-t hairline">
    <Reveal><Overline color={TEAL}>Who should join?</Overline></Reveal>
    <Reveal><h2 className="text-3xl md:text-5xl font-bold tracking-tight max-w-2xl leading-tight">Perfect for builders of every background.</h2></Reveal>
    <div className="flex flex-wrap gap-3 mt-12">
      {audiences.map((a, i) => (
        <Reveal key={a} delay={(i % 4) * 0.05}>
          <span className="inline-flex items-center gap-2 rounded-full border hairline bg-white/[0.03] px-5 py-3 text-neutral-200">
            <span className="h-1.5 w-1.5 rounded-full" style={{ background: i % 2 ? TEAL : GOLD }} />
            {a}
          </span>
        </Reveal>
      ))}
    </div>
    <p className="mt-8 text-neutral-400">No coding experience required.</p>
  </section>
);

/* ---------------- How BUILD Works ---------------- */
const steps = ["Apply", "Application Review", "Receive Invitation", "Join Live Session", "Build Together", "Ask Questions", "Deploy Your AI"];
const HowItWorks = () => (
  <section data-testid="build-how" className="max-w-5xl mx-auto px-5 md:px-8 py-24 md:py-32 border-t hairline">
    <Reveal><Overline>How BUILD works</Overline></Reveal>
    <div className="mt-12 grid sm:grid-cols-2 lg:grid-cols-4 gap-3">
      {steps.map((s, i) => (
        <Reveal key={s} delay={(i % 4) * 0.06}>
          <div className="h-full rounded-2xl border hairline bg-white/[0.03] p-5">
            <span className="inline-flex h-9 w-9 items-center justify-center rounded-full text-sm font-semibold text-black" style={{ background: i % 2 ? TEAL : GOLD }}>
              {i + 1}
            </span>
            <p className="mt-4 font-medium leading-snug">{s}</p>
          </div>
        </Reveal>
      ))}
    </div>
  </section>
);

/* ---------------- What You’ll Leave With ---------------- */
const takeaways = [
  "Working AI Voice Agent", "Understanding of modern AI tools", "AI workflow knowledge",
  "Prompt Engineering fundamentals", "CRM integration basics", "Confidence to build future AI applications",
];
const LeaveWith = () => (
  <section data-testid="build-takeaways" className="max-w-5xl mx-auto px-5 md:px-8 py-24 md:py-32 border-t hairline">
    <Reveal><Overline color={TEAL}>What you’ll leave with</Overline></Reveal>
    <div className="grid sm:grid-cols-2 gap-4 mt-12">
      {takeaways.map((item, i) => (
        <Reveal key={item} delay={(i % 2) * 0.08}>
          <div className="flex items-center gap-4 rounded-2xl border hairline bg-white/[0.03] px-6 py-5">
            <span className="inline-flex h-8 w-8 items-center justify-center rounded-full text-black shrink-0" style={{ background: GOLD }}>
              <Check size={16} />
            </span>
            <p className="font-medium">{item}</p>
          </div>
        </Reveal>
      ))}
    </div>
  </section>
);

/* ---------------- Future Builds ---------------- */
const FutureBuilds = () => (
  <section data-testid="build-future" className="max-w-5xl mx-auto px-5 md:px-8 py-24 md:py-32 border-t hairline">
    <Reveal><Overline>Future Builds</Overline></Reveal>
    <div className="grid sm:grid-cols-3 gap-4 mt-12">
      {["002", "003", "004"].map((n, i) => (
        <Reveal key={n} delay={i * 0.08}>
          <div className="rounded-2xl border hairline bg-white/[0.03] p-8 h-full">
            <span className="text-xs uppercase tracking-[0.28em] text-neutral-500">BUILD // {n}</span>
            <p className="mt-4 text-xl font-medium text-neutral-400">Coming Soon</p>
          </div>
        </Reveal>
      ))}
    </div>
  </section>
);

/* ---------------- Become a BUILD Partner (Refer & Earn) ---------------- */
const PartnerWaitlist = () => {
  const [form, setForm] = useState({ name: "", email: "", whatsapp: "", note: "" });
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const update = (e) => setForm((f) => ({ ...f, [e.target.name]: e.target.value }));
  const canSubmit = form.name && form.email && !loading;

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await axios.post(`${API}/build/partners`, form);
      setDone(true);
    } catch {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const inputCls = "w-full bg-transparent border-b border-white/15 py-2.5 placeholder:text-neutral-600 focus:border-white outline-none transition-colors duration-300";

  return (
    <section data-testid="build-partner" className="max-w-5xl mx-auto px-5 md:px-8 py-24 md:py-32 border-t hairline">
      <Reveal>
        <div className="rounded-3xl border hairline overflow-hidden p-8 md:p-12" style={{ background: "linear-gradient(135deg, rgba(94,200,190,0.06), rgba(217,164,65,0.05))" }}>
          <div className="grid md:grid-cols-2 gap-12 md:gap-16 items-start">
            <div>
              <span className="inline-flex items-center gap-2 rounded-full border hairline px-3 py-1 text-xs uppercase tracking-[0.2em] text-neutral-400">
                <span className="h-1.5 w-1.5 rounded-full animate-pulse" style={{ background: GOLD }} />
                Refer &amp; Earn · Coming Soon
              </span>
              <h2 className="text-3xl md:text-5xl font-bold tracking-tight leading-tight mt-6">Become a BUILD Partner.</h2>
              <p className="mt-6 text-lg text-neutral-400 leading-relaxed max-w-md">
                Love BUILD? Soon you&rsquo;ll be able to refer others and earn rewards for every builder you bring in. Join the waitlist and be first in line when the Partner program opens.
              </p>
            </div>

            {done ? (
              <div data-testid="partner-success" className="border hairline rounded-2xl py-14 text-center bg-black/20">
                <span className="inline-flex h-14 w-14 items-center justify-center rounded-full mx-auto" style={{ background: "rgba(94,200,190,0.12)", color: TEAL }}>
                  <CheckCircle2 size={26} />
                </span>
                <p className="text-xl font-semibold mt-5">You&rsquo;re on the waitlist!</p>
                <p className="text-neutral-400 mt-2 max-w-xs mx-auto">I&rsquo;ll email you the moment the Refer &amp; Earn program goes live.</p>
              </div>
            ) : (
              <form onSubmit={submit} data-testid="partner-form" className="space-y-6">
                <div>
                  <label className="block text-xs uppercase tracking-[0.15em] text-neutral-500 mb-2">Name *</label>
                  <input data-testid="pt-name" name="name" required value={form.name} onChange={update} className={inputCls} placeholder="Your name" />
                </div>
                <div>
                  <label className="block text-xs uppercase tracking-[0.15em] text-neutral-500 mb-2">Email *</label>
                  <input data-testid="pt-email" type="email" name="email" required value={form.email} onChange={update} className={inputCls} placeholder="you@email.com" />
                </div>
                <div>
                  <label className="block text-xs uppercase tracking-[0.15em] text-neutral-500 mb-2">WhatsApp</label>
                  <input data-testid="pt-whatsapp" name="whatsapp" value={form.whatsapp} onChange={update} className={inputCls} placeholder="+91 98765 43210" />
                </div>
                <div>
                  <label className="block text-xs uppercase tracking-[0.15em] text-neutral-500 mb-2">How will you refer? (optional)</label>
                  <textarea data-testid="pt-note" name="note" rows={2} value={form.note} onChange={update} className={`${inputCls} resize-none`} placeholder="e.g. my community, audience, network…" />
                </div>
                <button
                  type="submit"
                  data-testid="pt-submit"
                  disabled={!canSubmit}
                  className="inline-flex items-center gap-2 rounded-full bg-white text-black text-sm font-medium px-8 py-3.5 hover:bg-neutral-200 transition-colors duration-300 disabled:opacity-40 disabled:cursor-not-allowed"
                >
                  {loading ? "Joining…" : "Join the waitlist"} {!loading && <ArrowRight size={16} />}
                </button>
              </form>
            )}
          </div>
        </div>
      </Reveal>
    </section>
  );
};

/* ---------------- Help Shape BUILD ---------------- */
const HelpShape = () => {
  const [form, setForm] = useState({ name: "", email: "", suggestion: "" });
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const update = (e) => setForm((f) => ({ ...f, [e.target.name]: e.target.value }));
  const canSubmit = form.name && form.email && form.suggestion && !loading;

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await axios.post(`${API}/build/suggestions`, form);
      setDone(true);
    } catch {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const inputCls = "w-full bg-transparent border-b border-white/15 py-2.5 placeholder:text-neutral-600 focus:border-white outline-none transition-colors duration-300";

  return (
    <section data-testid="build-help-shape" className="max-w-5xl mx-auto px-5 md:px-8 py-24 md:py-32 border-t hairline">
      <div className="grid md:grid-cols-2 gap-12 md:gap-16 items-start">
        <Reveal>
          <Overline color={TEAL}>Help shape BUILD</Overline>
          <h2 className="text-3xl md:text-5xl font-bold tracking-tight leading-tight">What should BUILD // 002 be?</h2>
          <p className="mt-6 text-lg text-neutral-400 leading-relaxed max-w-md">
            Tell me which AI application you’d love to build next. Your ideas directly shape the roadmap.
          </p>
        </Reveal>
        <Reveal delay={0.1}>
          {done ? (
            <div data-testid="suggestion-success" className="border hairline rounded-2xl py-14 text-center">
              <span className="inline-flex h-14 w-14 items-center justify-center rounded-full mx-auto" style={{ background: "rgba(94,200,190,0.12)", color: TEAL }}>
                <CheckCircle2 size={26} />
              </span>
              <p className="text-xl font-semibold mt-5">Thanks for the idea!</p>
              <p className="text-neutral-400 mt-2">I read every suggestion personally.</p>
            </div>
          ) : (
            <form onSubmit={submit} data-testid="suggestion-form" className="space-y-6">
              <div>
                <label className="block text-xs uppercase tracking-[0.15em] text-neutral-500 mb-2">Name</label>
                <input data-testid="sg-name" name="name" required value={form.name} onChange={update} className={inputCls} placeholder="Your name" />
              </div>
              <div>
                <label className="block text-xs uppercase tracking-[0.15em] text-neutral-500 mb-2">Email</label>
                <input data-testid="sg-email" type="email" name="email" required value={form.email} onChange={update} className={inputCls} placeholder="you@email.com" />
              </div>
              <div>
                <label className="block text-xs uppercase tracking-[0.15em] text-neutral-500 mb-2">Suggestion</label>
                <textarea data-testid="sg-suggestion" name="suggestion" required rows={3} value={form.suggestion} onChange={update} className={`${inputCls} resize-none`} placeholder="What should we build next?" />
              </div>
              <button
                type="submit"
                data-testid="sg-submit"
                disabled={!canSubmit}
                className="inline-flex items-center gap-2 rounded-full bg-white text-black text-sm font-medium px-8 py-3.5 hover:bg-neutral-200 transition-colors duration-300 disabled:opacity-40 disabled:cursor-not-allowed"
              >
                {loading ? "Sending…" : "Send suggestion"} {!loading && <ArrowRight size={16} />}
              </button>
            </form>
          )}
        </Reveal>
      </div>
    </section>
  );
};

/* ---------------- FAQ ---------------- */
const faqs = [
  { q: "Do I need coding experience?", a: "No. BUILD is beginner friendly and designed so anyone can follow along. We focus on understanding the workflow and using modern AI tools — not writing complex code." },
  { q: "Can I customize it for my own business?", a: "Yes. You’ll build with your own script, your own qualification questions, and your own use case, so the AI Voice Agent fits your business from day one." },
  { q: "Will I receive a recording?", a: "Yes. You’ll get a recording of the live session so you can revisit each step whenever you need." },
  { q: "Can I ask questions?", a: "Absolutely. BUILD is live and guided — you can ask questions throughout and we’ll solve challenges together in real time." },
  { q: "Will I receive support?", a: "Yes. You’ll get guidance during the build and follow-up support to help you deploy and improve your AI application." },
];
const FAQ = () => {
  const [openIdx, setOpenIdx] = useState(0);
  return (
    <section data-testid="build-faq" className="max-w-5xl mx-auto px-5 md:px-8 py-24 md:py-32 border-t hairline">
      <Reveal><Overline>FAQ</Overline></Reveal>
      <Reveal><h2 className="text-3xl md:text-5xl font-bold tracking-tight max-w-2xl leading-tight">Questions, answered.</h2></Reveal>
      <div className="mt-12 max-w-3xl divide-y divide-white/10 border-y hairline">
        {faqs.map((f, i) => {
          const isOpen = openIdx === i;
          return (
            <div key={f.q} data-testid={`faq-item-${i}`}>
              <button
                type="button"
                data-testid={`faq-toggle-${i}`}
                onClick={() => setOpenIdx(isOpen ? -1 : i)}
                className="w-full flex items-center justify-between gap-4 py-6 text-left"
              >
                <span className="text-lg font-medium">{f.q}</span>
                <span className="shrink-0 text-neutral-400">{isOpen ? <Minus size={18} /> : <Plus size={18} />}</span>
              </button>
              <AnimatePresence initial={false}>
                {isOpen && (
                  <motion.div
                    initial={{ height: 0, opacity: 0 }} animate={{ height: "auto", opacity: 1 }} exit={{ height: 0, opacity: 0 }}
                    transition={{ duration: 0.3, ease: [0.16, 1, 0.3, 1] }}
                    className="overflow-hidden"
                  >
                    <p className="pb-6 text-neutral-400 leading-relaxed">{f.a}</p>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          );
        })}
      </div>
    </section>
  );
};

/* ---------------- Final CTA ---------------- */
const FinalCTA = () => (
  <section data-testid="build-final-cta" className="border-t hairline">
    <div className="max-w-5xl mx-auto px-5 md:px-8 py-28 md:py-40 text-center">
      <Reveal>
        <h2 className="text-4xl md:text-6xl font-bold tracking-[-0.02em] leading-tight">Ready to Build?</h2>
        <p className="mt-6 text-lg text-neutral-400 max-w-xl mx-auto">Stop watching tutorials. Start building.</p>
        <div className="mt-10 flex justify-center"><ApplyButton testid="final-apply" /></div>
      </Reveal>
    </div>
  </section>
);

/* ---------------- Footer ---------------- */
const legalLinks = [
  { to: "/privacy", label: "Privacy Policy" },
  { to: "/terms", label: "Terms & Conditions" },
  { to: "/cookies", label: "Cookie Policy" },
  { to: "/refund", label: "Refund Policy" },
];
const Footer = () => (
  <footer data-testid="build-footer" className="border-t hairline">
    <div className="max-w-5xl mx-auto px-5 md:px-8 py-14">
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-8">
        <div>
          <Link to="/" className="font-semibold hover:text-white transition-colors duration-300">Anubhav Sahni</Link>
          <p className="text-sm text-neutral-500 mt-1">© 2026 Anubhav Sahni. All rights reserved.</p>
        </div>
        <div className="flex flex-wrap items-center gap-x-6 gap-y-3 text-sm text-neutral-400">
          {legalLinks.map((l) => (
            <Link key={l.to} to={l.to} data-testid={`footer-${l.to.slice(1)}`} className="hover:text-white transition-colors duration-300">{l.label}</Link>
          ))}
          <a href={`mailto:${SITE.email}`} data-testid="footer-contact" className="hover:text-white transition-colors duration-300">Contact</a>
        </div>
      </div>
    </div>
  </footer>
);

export default function Build() {
  const [modalOpen, setModalOpen] = useState(false);
  const title = "BUILD — Learn AI by Building Real Applications | Anubhav Sahni";
  const description =
    "BUILD is a live, guided experience where you build a real AI application from scratch. Start with BUILD // 001 — build your first AI Voice Agent. No coding experience required.";
  const url = `${SITE.url}/build`;
  return (
    <ApplyContext.Provider value={() => setModalOpen(true)}>
      <main className="bg-[#08080A] text-[#EDEDED]" dir="ltr">
        <Helmet>
          <html lang="en" dir="ltr" />
          <title>{title}</title>
          <meta name="description" content={description} />
          <meta name="robots" content="index, follow, max-image-preview:large" />
          <link rel="canonical" href={url} />
          <meta property="og:type" content="website" />
          <meta property="og:title" content={title} />
          <meta property="og:description" content={description} />
          <meta property="og:url" content={url} />
          <meta name="twitter:card" content="summary_large_image" />
          <meta name="twitter:title" content={title} />
          <meta name="twitter:description" content={description} />
        </Helmet>
        <Nav />
        <Hero />
        <Blueprint />
        <FeaturedBand />
        <WhatIsBuild />
        <WhatYoullLearn />
        <WhatYoullBuild />
        <ImagineThis />
        <WhyBuild />
        <WhoShouldJoin />
        <HowItWorks />
        <LeaveWith />
        <FutureBuilds />
        <PartnerWaitlist />
        <HelpShape />
        <FAQ />
        <FinalCTA />
        <Footer />
      </main>
      <ApplyModal open={modalOpen} onClose={() => setModalOpen(false)} />
    </ApplyContext.Provider>
  );
}
